import { Button, Chip } from '@emailforge/ui';

import { Icon } from '@/components/ui/icons';
import {
  type PreflightCheck,
  type PreflightReport,
  useRunPreflightMutation,
} from '@/features/templates/templatesApi';

/**
 * Modal showing a preflight report — runs image-weight, spam-word, and
 * variable-validation checks server-side. Triggered from the editor toolbar.
 * Re-run button kicks off the same endpoint again.
 */
export function PreflightModal({
  templateId,
  open,
  onClose,
}: {
  templateId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [run, { data: report, isLoading, error }] = useRunPreflightMutation({
    fixedCacheKey: `preflight-${templateId}`,
  });

  if (!open) return null;

  return (
    <div
      role="dialog"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'oklch(0.18 0.01 85 / 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        className="mf-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 640,
          maxHeight: '80vh',
          background: 'var(--paper)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--line-soft)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <h2 className="display" style={{ margin: 0, fontSize: 22, letterSpacing: -0.01 }}>
            Preflight
          </h2>
          {report && <Summary report={report} />}
          <div style={{ flex: 1 }} />
          <Button size="sm" onClick={() => run(templateId)} disabled={isLoading}>
            {isLoading ? 'Running…' : report ? 'Re-run' : 'Run checks'}
          </Button>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--ink-3)',
              padding: 4,
            }}
          >
            <Icon.x size={16} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: 20 }}>
          {!report && !isLoading && !error && (
            <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.55 }}>
              Click <strong>Run checks</strong> to scan for oversized images, spam-trigger language,
              and variable mismatches against the latest version.
            </div>
          )}
          {isLoading && (
            <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
              Running checks (HEAD requests for image weight may take a few seconds)…
            </div>
          )}
          {error && (
            <div
              style={{
                padding: 12,
                background: 'var(--err-soft)',
                border: '1px solid oklch(0.85 0.10 25)',
                borderRadius: 'var(--r-2)',
                color: 'var(--err)',
                fontSize: 13,
              }}
            >
              Could not run preflight. The API may be offline.
            </div>
          )}
          {report && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {report.checks.map((c) => (
                <CheckRow key={c.id} check={c} />
              ))}
            </div>
          )}
        </div>

        {report && (
          <div
            style={{
              padding: '10px 20px',
              borderTop: '1px solid var(--line-soft)',
              fontSize: 11,
              color: 'var(--ink-3)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            Ran in {report.durationMs}ms · version {report.versionId.slice(0, 8)}
          </div>
        )}
      </div>
    </div>
  );
}

function Summary({ report }: { report: PreflightReport }) {
  const { ok, warn, fail } = report.summary;
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {fail > 0 && <Chip variant="err">{fail} fail</Chip>}
      {warn > 0 && <Chip variant="flame">{warn} warn</Chip>}
      {ok > 0 && fail === 0 && warn === 0 && <Chip variant="live">{ok} ok</Chip>}
    </div>
  );
}

function CheckRow({ check }: { check: PreflightCheck }) {
  const tone =
    check.status === 'ok' ? 'live' : check.status === 'warn' ? 'flame' : 'err';
  return (
    <div
      className="mf-card"
      style={{
        padding: 12,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        borderColor:
          check.status === 'fail'
            ? 'var(--err)'
            : check.status === 'warn'
              ? 'var(--flame-line)'
              : 'var(--line)',
      }}
    >
      <Chip variant={tone}>{check.status}</Chip>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 550, marginBottom: 4 }}>{check.title}</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
          {check.message}
        </div>
        {check.details && Object.keys(check.details).length > 0 && (
          <details style={{ marginTop: 6 }}>
            <summary
              className="mono"
              style={{
                fontSize: 10.5,
                color: 'var(--ink-3)',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: 0.06,
              }}
            >
              details
            </summary>
            <pre
              className="mono"
              style={{
                fontSize: 11,
                background: 'var(--paper-2)',
                padding: 8,
                borderRadius: 4,
                marginTop: 6,
                overflowX: 'auto',
              }}
            >
              {JSON.stringify(check.details, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
